import {
    createSensor,
    fetchSensors,
    updateSensor,
    useFridges,
    useSensor,
    useSensorForm,
} from '@gozoki/api';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Button, IconButton, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useToast } from '../../components/communication/Toast';
import LoadingPaper from '../../components/containers/LoadingPaper';
import SelectRhf from '../../components/inputs/SelectRhf';
import TextFieldRhf from '../../components/inputs/TextFieldRhf';
import Page from '../../components/Page';
import { ROUTES } from '../../utils/constants/routes.constants';
import { ThemeColors } from '../../utils/theme/colors.theme';
import { fonts } from '../../utils/theme/fonts.theme';
import { ButtonOverrides } from '../../utils/theme/overrides.theme';

const AddSensorPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();
    const { id } = useParams();
    const updateMode = id !== undefined; // Update mode if there is a sensor ID in the URL
    const [Toast, showToast] = useToast();

    // If this is update mode, use isLoading to show a loading indicator before the data arrives
    const sensorId = id ? parseInt(id, 10) : undefined;
    const { data, isLoading } = useSensor(parseInt(id ?? '', 10));

    const { control, trigger, getValues, setValue, setError } = useSensorForm();

    const { data: fridges = [] } = useFridges();

    // Fill the form with initial data once the data is fetched
    useEffect(() => {
        // Set all values
        if (data) {
            setValue('reference', data.reference || '');
            setValue('label', data.label);
            setValue('description', data.description || '');
            setValue('model', data.model || '');
            if (data.fridge?.id) {
                setValue('fridgeId', data.fridge.id);
            }
        }
    }, [setValue, data]);

    // Preserve tab parameter when navigating back
    const navigateBack = () => {
        const searchParams = new URLSearchParams(location.search);
        const tab = searchParams.get('tab');
        const url = tab ? `${ROUTES.SENSORS}?tab=${tab}` : ROUTES.SENSORS;
        navigate(url);
    };

    // ******************************************************************** //
    //                              MUTATIONS                               //
    // ******************************************************************** //

    const { mutate: upload, isPending: isUploading } = useMutation({
        mutationFn: createSensor,
        onSuccess: () => {
            // Invalidate all sensors queries (for all store filters)
            queryClient.invalidateQueries({ queryKey: ['sensors'] });
            navigateBack();
        },
        onError: (error: unknown) => {
            const errorData =
                error instanceof AxiosError &&
                error.response?.data &&
                typeof error.response.data === 'object'
                    ? (error.response.data as Record<string, unknown>)
                    : null;

            if (errorData?.code === '23505') {
                setError('label', {
                    type: 'manual',
                    message: 'Un capteur avec ce libellé existe déjà pour ce réfrigérateur.',
                });
                return;
            }

            showToast({
                message: 'Une erreur est survenue lors de la création du capteur',
                severity: 'error',
            });
        },
    });

    const { mutate: update, isPending: isUpdating } = useMutation({
        mutationFn: updateSensor,
        onSuccess: () => {
            // Invalidate all sensors queries and the specific sensor query
            queryClient.invalidateQueries({ queryKey: ['sensors'] });
            queryClient.invalidateQueries({ queryKey: ['sensor', parseInt(id ?? '', 10)] });
            navigateBack();
        },
        onError: (error: unknown) => {
            const errorData =
                error instanceof AxiosError &&
                error.response?.data &&
                typeof error.response.data === 'object'
                    ? (error.response.data as Record<string, unknown>)
                    : null;

            if (errorData?.code === '23505') {
                setError('label', {
                    type: 'manual',
                    message: 'Un capteur avec ce libellé existe déjà pour ce réfrigérateur.',
                });
                return;
            }

            showToast({
                message: 'Une erreur est survenue lors de la modification du capteur',
                severity: 'error',
            });
        },
    });

    // ******************************************************************** //
    //                               HANDLERS                               //
    // ******************************************************************** //

    const uploadSensor = async () => {
        const valid = await trigger(); // Trigger form validation

        if (valid) {
            const values = getValues();
            // Check uniqueness: (fridgeId, label) must be unique
            try {
                const sensors = await fetchSensors();
                const duplicate = sensors.find(
                    (sensor) =>
                        sensor.fridge?.id === values.fridgeId && sensor.label === values.label
                );
                if (duplicate) {
                    setError('label', {
                        type: 'manual',
                        message: 'Un capteur avec ce libellé existe déjà pour ce réfrigérateur.',
                    });
                    return;
                }
            } catch (error) {
                // If the API call fails, proceed anyway (backend will validate)
            }
            upload(values);
        }
    };

    const handleUpdateSensor = async () => {
        const valid = await trigger(); // Trigger form validation

        if (valid) {
            const values = getValues();
            // Check uniqueness: (fridgeId, label) must be unique (excluding current sensor)
            try {
                const sensors = await fetchSensors();
                const duplicate = sensors.find(
                    (sensor) =>
                        sensor.fridge?.id === values.fridgeId &&
                        sensor.label === values.label &&
                        sensor.id !== sensorId
                );
                if (duplicate) {
                    setError('label', {
                        type: 'manual',
                        message: 'Un capteur avec ce libellé existe déjà pour ce réfrigérateur.',
                    });
                    return;
                }
            } catch (error) {
                // If the API call fails, proceed anyway (backend will validate)
            }
            update({
                id: parseInt(id!, 10),
                data: values,
            });
        }
    };

    return (
        <Page sx={{ maxWidth: '50vw', margin: 'auto' }}>
            <Toast />
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <IconButton onClick={navigateBack}>
                    <ArrowBackIcon
                        sx={{
                            color: ThemeColors.BLACK,
                            height: '24px',
                            width: '24px',
                        }}
                    />
                </IconButton>
                <Typography style={{ ...fonts.pageSubtitle, marginLeft: '16px' }} flexGrow={1}>
                    {updateMode ? 'Modifier un capteur' : 'Ajouter un capteur'}
                </Typography>

                <Button
                    variant="outlined"
                    sx={{ marginRight: '16px', ...ButtonOverrides.blackOutlined }}
                    onClick={navigateBack}
                >
                    Annuler
                </Button>
                <Button
                    variant="contained"
                    disabled={isUpdating || isUploading}
                    onClick={updateMode ? handleUpdateSensor : uploadSensor}
                >
                    {updateMode ? 'Modifier' : 'Ajouter'}
                </Button>
            </div>
            <LoadingPaper
                sx={{ marginTop: '40px', padding: '24px', paddingBottom: '40px' }}
                loading={isLoading && updateMode}
            >
                <Typography mb="8px" style={fonts.inputHeader}>
                    Référence (devEUI)
                </Typography>
                <TextFieldRhf
                    variant="outlined"
                    placeholder="Référence (devEUI) (optionnel)"
                    color="info"
                    control={control}
                    trigger={trigger}
                    name="reference"
                    fullWidth
                />
                <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
                    Libellé *
                </Typography>
                <TextFieldRhf
                    variant="outlined"
                    placeholder="Libellé du capteur"
                    color="info"
                    control={control}
                    trigger={trigger}
                    name="label"
                    fullWidth
                />
                <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
                    Description
                </Typography>
                <TextFieldRhf
                    variant="outlined"
                    placeholder="Description (optionnel)"
                    color="info"
                    control={control}
                    trigger={trigger}
                    name="description"
                    fullWidth
                    multiline
                    rows={3}
                />
                <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
                    Modèle
                </Typography>
                <TextFieldRhf
                    variant="outlined"
                    placeholder="Modèle du capteur"
                    color="info"
                    control={control}
                    trigger={trigger}
                    name="model"
                    fullWidth
                />
                <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
                    Réfrigérateur *
                </Typography>
                <SelectRhf
                    control={control}
                    name="fridgeId"
                    options={fridges.map((fridge) => ({
                        value: fridge.id,
                        label: `${fridge.label} (${fridge.type})${
                            fridge.store ? ` - ${fridge.store.label}` : ''
                        }`,
                    }))}
                />
            </LoadingPaper>
        </Page>
    );
};

export default AddSensorPage;
